import React, { useEffect, useRef, useState } from 'react';
import './ValueProp.css';

const ValueProp = () => {
  const sectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState('onboard');
  const [sectionVisible, setSectionVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Reset current slide when changing tabs
    setCurrentSlide(0);
  }, [activeTab]);

  useEffect(() => {
    // Observer for the main section visibility
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !sectionVisible) {
            setSectionVisible(true);
            sectionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 } // Trigger when 20% of the section is visible
    );

    // Start observing the section
    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }

    // Observer for the animation elements (only starts if section is visible)
    const animateElements = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      const elements = sectionRef.current.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    };

    // Only activate animations if section is visible
    let animationObserver;
    if (sectionVisible) {
      animationObserver = animateElements();
    }

    return () => {
      if (sectionRef.current) {
        sectionObserver.unobserve(sectionRef.current);
      }
      if (animationObserver) {
        animationObserver();
      }
    };
  }, [sectionVisible]);

  const tabContent = {
    onboard: [
      {
        image: './project_plan.png',
        title: 'Map Out Projects with a Conversation',
        description: 'Talk or type with the AI to define, refine and break down complex projects.'
      },
      {
        image: './project_roadmap.png',
        title: "Start with a Bird's Eye View",
        description: "AI generated project roadmaps and deliverables let's you instantly see where you're headed."
      },
      {
        image: './step_by_step.png',
        title: 'Get a Step-by-Step Playbook',
        description: "Remove overwhelm by breaking down your big, complex project into small, actionable tasks."
      }
    ],
    guidance: [
      {
        image: './thought_partner.png',
        title: 'Unblock in Real Time',
        description: "Attenxion offers instant, context-aware support to help you push through roadblocks without getting frustrated."
      },
      {
        image: './task_breakdown.png',
        title: 'Automatically Break Down Tasks',
        description: 'Use AI to break down tasks to your desired level of detail.'
      },
      {
        image: './multimodal_input.png',
        title: 'Multimodal Input Support',
        description: 'Switch seamlessly between voice, text, screen sharing, or file uploads for flexible, frictionless support.'
      }
    ],
    hyperfocus: [
      {
        image: './pomodoro_start.png',
        title: 'Immerse in Deep Work',
        description: 'Attenxion creates a distraction-free workspace with Pomodoro timers, focus music, and visual cues — helping you stay locked in and on task.'
      },
      {
        image: './dashboard.png',
        title: 'Track Flow States',
        description: 'Identify your most productive times and capitalize on your natural energy cycles.'
      },
      {
        image: './procrastination.png',
        title: 'Overcome Procrastination',
        description: 'Smart reminders and personalized motivation techniques help you push through periods of low motivation.'
      }
    ],
    progress: [
      {
        image: './progress_tracking.png',
        title: 'Stay Oriented and on Track',
        description: "Visual tracking shows how far you’ve come, what’s next, and how close you are to the end."
      },
      {
        image: './personal_time_estimates.png',
        title: 'Personalized Time Estimates',
        description: 'Smart time estimates tailored to your past work and pace.'
      },
      {
        image: './pomodoro_end.png',
        title: 'Celebrate Progress, Sustain Momentum',
        description: 'Motivation built in with visuals and encouragement that celebrate every milestone.'
      }  
    ],
    collaboration: [
      {
        image: './cross_collaboration.png',
        title: 'Align Around Shared Goals',
        description: "Before work begins, teams align on clear deliverables using AI-powered visuals."
      },
      {
        image: './deliverables.png',
        title: "Edit Deliverables Together",
        description: "Team members can refine, discuss, and evolve project deliverables until everyone agrees on what success looks like."
      },
      {
        image: './version_control.png',
        title: "Unified Project Context",
        description: "Attenxion keeps track of every update—across tools, files, and teammates—so your AI always understands the full project picture."
      }
    ]
  };

  const changeSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    
    setIsAnimating(true);
    const contentEl = document.querySelector('.feature-content');
    const imageEl = document.querySelector('.feature-image');
    
    if (contentEl && imageEl) {
      // Fade out
      contentEl.style.opacity = '0';
      imageEl.style.opacity = '0';
      
      // Change slide after fade out
      setTimeout(() => {
        setCurrentSlide(index);
        
        // Fade in
        setTimeout(() => {
          contentEl.style.opacity = '1';
          imageEl.style.opacity = '1';
          setTimeout(() => setIsAnimating(false), 300);
        }, 50);
      }, 300);
    } else {
      setCurrentSlide(index);
      setIsAnimating(false);
    }
  };

  const nextSlide = () => {
    const slides = tabContent[activeTab];
    const nextIndex = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    changeSlide(nextIndex);
  };

  const prevSlide = () => {
    const slides = tabContent[activeTab];
    const prevIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    changeSlide(prevIndex);
  };

  const renderTabContent = () => {
    const slides = tabContent[activeTab];
    const content = slides[currentSlide];
    
    return (
      <div className="features-container">
        <div className="feature-row animate-on-scroll">
          {slides.length > 1 && (
            <>
              <button 
                className="carousel-arrow prev" 
                onClick={prevSlide}
                aria-label="Previous feature"
                disabled={isAnimating}
              >
                &#10094;
              </button>
              
              <div className="carousel-indicators">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => changeSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    disabled={isAnimating}
                  />
                ))}
              </div>
            </>
          )}
          
          <div className="feature-content">
            <h2>{content.title}</h2>
            <p>{content.description}</p>
          </div>
          
          <div className="feature-image">
            <img src={content.image} alt={content.title} />
          </div>
          
          {slides.length > 1 && (
            <button 
              className="carousel-arrow next" 
              onClick={nextSlide}
              aria-label="Next feature"
              disabled={isAnimating}
            >
              &#10095;
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="block" ref={sectionRef} id="value-prop">
      <div className="content-container">
        <div className="value-prop-header animate-on-scroll">
          <h1>Optimized for Teams.<br/>Personalized for Impact.</h1>
          <p>How Attenxion Helps People Do Their Best Work</p>
        </div>

        <nav className="tabs-navigation">
          <button 
            className={`tab-item ${activeTab === 'onboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('onboard')}
          >
            Easy Onboarding
          </button>
          <button 
            className={`tab-item ${activeTab === 'guidance' ? 'active' : ''}`}
            onClick={() => setActiveTab('guidance')}
          >
            Instant Support
          </button>
          <button 
            className={`tab-item ${activeTab === 'hyperfocus' ? 'active' : ''}`}
            onClick={() => setActiveTab('hyperfocus')}
          >
            Unlock Deep Focus
          </button>
          <button 
            className={`tab-item ${activeTab === 'progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            Track Progress 
          </button>
          <button 
            className={`tab-item ${activeTab === 'collaboration' ? 'active' : ''}`}
            onClick={() => setActiveTab('collaboration')}
          >
            Team Collaboration
          </button>
        </nav>

        {renderTabContent()}
      </div>
    </section>
  );
};

export default ValueProp; 